import { useState } from 'react';
import { peso } from '../../../../storefront/lib/format';
import type { DisplayProduct } from '../../../../storefront/lib/types';
import { ImageSlot } from '../interactive';
import { FONT_DISPLAY } from '../tokens';
import { useFlxShop } from './catalog';
import { isSoldOut, useAddToCart } from './use-add-to-cart';

/**
 * The rest of the house range (membership kits, gear), full-width under the
 * flavour shelf in section 02.
 *
 * Renders NOTHING while the catalog is just the two launch pouches — which is
 * where FLX is today, and is what keeps the page true to the approved design —
 * and appears on its own the day a kit or a piece of gear is added, so the MLM
 * side stays sellable without a second redesign.
 */
export function FlxCatalogGrid() {
  const [category, setCategory] = useState<string | null>(null);
  const { products, chips, isEmpty } = useFlxShop(category);

  if (isEmpty || products.length === 0) return null;

  return (
    <div style={{ marginTop: 96 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 24,
          flexWrap: 'wrap',
          marginBottom: 32,
        }}
      >
        <div>
          <div
            style={{
              fontFamily: FONT_DISPLAY,
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: '.32em',
              textTransform: 'uppercase',
              opacity: 0.5,
              marginBottom: 8,
            }}
          >
            The Full Catalog
          </div>
          <h3
            style={{
              fontFamily: FONT_DISPLAY,
              fontWeight: 900,
              fontSize: 'clamp(32px,3.4vw,52px)',
              lineHeight: 0.92,
              textTransform: 'uppercase',
              fontStyle: 'italic',
              letterSpacing: '-.01em',
              margin: 0,
            }}
          >
            Everything FLX ships.
          </h3>
        </div>
        {chips.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <CategoryChip
              label="All Products"
              active={category === null}
              onClick={() => setCategory(null)}
            />
            {chips.map((c) => (
              <CategoryChip
                key={c.id}
                label={c.name}
                active={category === c.id}
                onClick={() => setCategory(c.id)}
              />
            ))}
          </div>
        )}
      </div>
      <div className="cat-grid">
        {products.map((p) => (
          <ProductCard key={p.id} product={p} />
        ))}
      </div>
    </div>
  );
}

/**
 * The live site's own `.cat-card`, as the flavour shelf above uses it, so the
 * catalog reads as part of section 02's paper band rather than a dark panel
 * dropped into it.
 */
function ProductCard({ product }: { product: DisplayProduct }) {
  const add = useAddToCart();
  const soldOut = isSoldOut(product);
  return (
    <article className="cat-card">
      <div className="cat-thumb">
        {product.image ? (
          <ImageSlot src={product.image} label={product.name} />
        ) : (
          <span className="cat-thumb-mark" aria-hidden="true">
            FLX
          </span>
        )}
      </div>
      <div className="cat-body">
        <div className="cat-flavor">{product.name}</div>
        <div className="cat-buy cat-buy--stack">
          <div className="cat-price">
            <span className="cat-price-now">{peso(product.price)}</span>
            {product.was > product.price && (
              <span className="cat-price-was">{peso(product.was)}</span>
            )}
          </div>
          <button
            type="button"
            className="btn btn--primary flx-add-btn"
            disabled={soldOut}
            onClick={() => add(product)}
          >
            {soldOut ? 'Sold Out' : 'Add to Cart'}
            {!soldOut && <span className="arrow">→</span>}
          </button>
        </div>
      </div>
    </article>
  );
}

function CategoryChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className="flx-chip" data-active={active || undefined}>
      {label}
    </button>
  );
}
